import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { Pill } from '../Pill.js';
import { STRATEGY_COLORS } from './strategies.js';

const BAR_SX = { px: { xs: 2, sm: 4 }, bgcolor: 'background.paper', borderColor: 'divider' };

interface GalleryProps {
  noun: string;
  ready: number;
  failed: number;
  readyLine: string;
  summary: string | null | undefined;
  gridTestId: string;
  onView: () => void;
  drawer: ReactNode;
  children: ReactNode;
}

interface DrawerContent {
  title: string;
  subtitle: string;
  tabs?: ReactNode;
  body: ReactNode;
  download: { href: string | null; fileName: string; label: string };
}

interface DrawerProps<T> {
  items: T[];
  active: number | null;
  noun: string;
  testId: string;
  onClose: () => void;
  onMove: (index: number) => void;
  render: (item: T) => DrawerContent;
}

interface CardProps {
  strategy: string;
  strategyLabel: string;
  title: string;
  subtitle: string;
  mediaSx: SxProps<Theme>;
  children: ReactNode;
  // Opens the card's detail from its caption, leaving clicks on the media to the media.
  onOpen?: () => void;
}

export function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

export function fileStem(name: string, fallback: string): string {
  const stem = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return stem || fallback;
}

export function GeneratedGallery({
  noun,
  ready,
  failed,
  readyLine,
  summary,
  gridTestId,
  onView,
  drawer,
  children,
}: GalleryProps): ReactNode {
  return (
    <Stack sx={{ gap: 2.5 }}>
      <Stack sx={{ gap: 0.5 }}>
        <Pill sx={{ alignSelf: 'flex-start', color: 'primary.main', bgcolor: 'primary.50' }}>
          ✦ THAT&apos;S A WRAP
        </Pill>
        <Typography variant="h2" sx={{ fontSize: 24 }}>
          {ready === 1 ? `Your ${noun} is ready` : `Your ${ready} ${noun}s are ready`}
        </Typography>
      </Stack>
      <Box
        data-testid={gridTestId}
        data-layout="responsive-three-column"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {children}
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ gap: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between', pt: 1 }}
      >
        <Stack>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{readyLine}</Typography>
          <Typography sx={{ fontSize: 12, color: failed > 0 ? 'error.main' : 'text.secondary' }}>
            {failed > 0 ? `${plural(failed, noun)} failed` : (summary ?? 'Saved to Outcomes')}
          </Typography>
        </Stack>
        <Button variant="gradient" disabled={ready === 0} onClick={onView}>
          View {noun}s
        </Button>
      </Stack>
      {drawer}
    </Stack>
  );
}

export function ResultCard({
  strategy,
  strategyLabel,
  title,
  subtitle,
  mediaSx,
  children,
  onOpen,
}: CardProps): ReactNode {
  const caption = (
    <Stack component="span" sx={{ gap: 0.5, p: 2 }}>
      <Typography
        component="span"
        sx={{
          display: 'block',
          fontSize: 10,
          fontWeight: 700,
          color: STRATEGY_COLORS[strategy] ?? 'text.secondary',
        }}
      >
        {strategyLabel.toUpperCase()}
      </Typography>
      <Typography component="span" sx={{ display: 'block', fontSize: 15, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography component="span" sx={{ display: 'block', fontSize: 12, color: 'text.secondary' }}>
        {subtitle}
      </Typography>
    </Stack>
  );
  return (
    <Stack
      sx={{
        minWidth: 0,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={[{ position: 'relative', overflow: 'hidden' }, ...[mediaSx].flat()]}>{children}</Box>
      {onOpen === undefined ? (
        caption
      ) : (
        <ButtonBase
          onClick={onOpen}
          sx={{
            display: 'block',
            textAlign: 'left',
            '&:hover': { bgcolor: 'action.hover' },
            // The card clips overflow, so draw the focus ring inside the caption.
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: -2,
            },
          }}
        >
          {caption}
        </ButtonBase>
      )}
    </Stack>
  );
}

export function FailedCard({ name, error }: { name: string; error: string | null }): ReactNode {
  return (
    <Alert severity="error">
      <AlertTitle>{name} could not be rendered</AlertTitle>
      {error ?? 'This version could not be rendered.'}
    </Alert>
  );
}

export function GeneratedDrawer<T>({
  items,
  active,
  noun,
  testId,
  onClose,
  onMove,
  render,
}: DrawerProps<T>): ReactNode {
  const item = active === null ? undefined : items[active];
  const content = item === undefined ? undefined : render(item);
  return (
    <Drawer
      anchor="right"
      open={content !== undefined}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 'min(820px, 100vw)' } } }}
    >
      {content === undefined || active === null ? null : (
        <Stack data-testid={testId} sx={{ minHeight: '100%', bgcolor: 'background.page' }}>
          <Stack sx={{ ...BAR_SX, pt: 3, pb: content.tabs ? 0 : 3, borderBottom: '1px solid' }}>
            <Stack direction="row" sx={{ gap: 2, alignItems: 'flex-start' }}>
              <Stack sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h3" sx={{ fontSize: 18 }}>
                  {content.title}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {content.subtitle}
                </Typography>
              </Stack>
              <IconButton
                aria-label={`Previous ${noun}`}
                disabled={active === 0}
                onClick={() => onMove(active - 1)}
                size="small"
              >
                <ArrowBackOutlined />
              </IconButton>
              <Typography sx={{ pt: 0.75, fontSize: 13 }}>
                {active + 1} / {items.length}
              </Typography>
              <IconButton
                aria-label={`Next ${noun}`}
                disabled={active === items.length - 1}
                onClick={() => onMove(active + 1)}
                size="small"
              >
                <ArrowForwardOutlined />
              </IconButton>
              <IconButton aria-label="Close drawer" onClick={onClose} size="small">
                <CloseOutlined />
              </IconButton>
            </Stack>
            {content.tabs}
          </Stack>
          <Stack sx={{ gap: 3, px: { xs: 2, sm: 4 }, py: 3, flex: 1, overflowY: 'auto' }}>
            {content.body}
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{
              ...BAR_SX,
              gap: 1.5,
              py: 2,
              justifyContent: 'flex-end',
              alignItems: { sm: 'center' },
              borderTop: '1px solid',
            }}
          >
            <Button onClick={onClose}>Close</Button>
            <Button
              component="a"
              href={content.download.href ?? undefined}
              download={content.download.fileName}
              disabled={content.download.href === null}
              variant="gradient"
              startIcon={<DownloadOutlined />}
            >
              {content.download.label}
            </Button>
          </Stack>
        </Stack>
      )}
    </Drawer>
  );
}
